import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/AuthContext'
import { loadIntercom, hideIntercomLauncher, showIntercomLauncher } from '@/lib/utils/intercom'

/**
 * Routes where the Intercom launcher (floating button) should be visible.
 * Include add-site + onboarding — that's where install blockers show up.
 */
const INTERCOM_ALLOWED_PREFIXES = [
  '/help',
  '/dashboard',
  '/add-website',
  '/onboarding',
]

function shouldShowIntercom(pathname: string): boolean {
  return INTERCOM_ALLOWED_PREFIXES.some(p => pathname.startsWith(p))
}

/**
 * Hook to initialize Intercom once and toggle launcher visibility by route.
 *
 * - Passes `hide_default_launcher` at load time so the launcher is never
 *   visible on blocked pages, even before the script finishes loading.
 * - On route change, updates launcher visibility via Intercom API.
 * - Polls briefly after init to catch the async script load.
 */
export const useIntercom = () => {
  const { user, token } = useAuth()
  const location = useLocation()
  const initialized = useRef(false)

  // Load Intercom once when authenticated
  useEffect(() => {
    if (!token || !user || initialized.current) return

    const shouldShow = shouldShowIntercom(location.pathname)

    const timer = setTimeout(() => {
      // Pass hideLauncher so Intercom boots with the correct state
      loadIntercom(user.email, user.userId, !shouldShow)
      initialized.current = true

      // Poll briefly to catch async script load and enforce state
      let attempts = 0
      const poll = setInterval(() => {
        attempts++
        if (window.Intercom && typeof window.Intercom === 'function') {
          const show = shouldShowIntercom(window.location.pathname)
          if (show) {
            showIntercomLauncher()
          } else {
            hideIntercomLauncher()
          }
          clearInterval(poll)
        }
        if (attempts >= 10) clearInterval(poll)
      }, 500)
    }, 2000)

    return () => clearTimeout(timer)
  }, [token, user])

  // Toggle launcher visibility on route change
  useEffect(() => {
    if (!initialized.current) return

    if (shouldShowIntercom(location.pathname)) {
      showIntercomLauncher()
    } else {
      hideIntercomLauncher()
    }
  }, [location.pathname])
}
